import { Controller } from "@hotwired/stimulus";
import { visit } from "@hotwired/turbo";

import { patch } from "../lib/http.js";
import { showToast } from "../lib/toast.js";

/**
 * Фильтр ленты по типам событий.
 *
 * Выбор применяется переходом по адресу с параметрами `types`, а владельцу
 * полки он ещё и сохраняется в настройках — чтобы лента открывалась такой же
 * в следующий раз.
 */
export default class extends Controller {
  static targets = ["dialog", "label", "checkbox"];
  static values = {
    selectedTypes: String,
    allLabel: { type: String, default: "Все события" },
    saveUrl: String,
    canPersist: Boolean,
  };

  connect() {
    this.selected = new Set(
      this.selectedTypesValue.split(",").map((type) => type.trim()).filter(Boolean),
    );
    this.appliedSelection = this.key();

    for (const checkbox of this.checkboxTargets) {
      checkbox.checked = this.selected.has(checkbox.dataset.type);
    }
    this.updateLabel();
  }

  open() {
    this.dialogTarget.showModal();
  }

  close() {
    this.dialogTarget.close();
  }

  /** Отмечает или снимает один тип события. */
  toggle(event) {
    const checkbox = event.currentTarget;
    const { type } = checkbox.dataset;
    if (!type) return;

    if (checkbox.checked) this.selected.add(type);
    else this.selected.delete(type);

    this.updateLabel();
  }

  /** Снимает все отметки. */
  clearSelection(event) {
    event.preventDefault();

    this.selected.clear();
    for (const checkbox of this.checkboxTargets) checkbox.checked = false;
    this.updateLabel();
  }

  /** Сохраняет выбор (если есть куда) и перезагружает ленту с фильтром. */
  async applySelection(event) {
    event.preventDefault();
    this.close();

    if (this.canPersistValue && this.hasSaveUrlValue && this.key() !== this.appliedSelection) {
      try {
        await patch(this.saveUrlValue, { activity_types: [...this.selected] });
      } catch (error) {
        // Фильтр всё равно применим — не сохранился только выбор по умолчанию.
        showToast(error.message);
      }
    }

    const url = new URL(window.location.href);
    for (const param of ["type", "types", "page"]) url.searchParams.delete(param);
    for (const type of this.selected) url.searchParams.append("types", type);

    // Переход через Turbo, а не через location: так лента едет без перезагрузки
    // страницы и показывает ту же плашку «Листаем страницу…», что и остальные
    // переходы.
    visit(url.toString());
  }

  /** Подпись кнопки: имя единственного типа либо число выбранных. */
  updateLabel() {
    const types = [...this.selected];
    let label = this.allLabelValue;

    if (types.length === 1) {
      const checkbox = this.checkboxTargets.find((target) => target.dataset.type === types[0]);
      label = checkbox?.dataset.label ?? "Выбрано: 1";
    } else if (types.length > 1) {
      label = `Выбрано: ${types.length}`;
    }

    if (this.hasLabelTarget) this.labelTarget.textContent = label;
    this.selectedTypesValue = types.join(",");
  }

  /** Сравнимое представление выбора: порядок отметок не важен. */
  key() {
    return [...this.selected].sort().join("|");
  }
}
